import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@/components/base/Button';
import Input from '@/components/base/Input';
import Alert from '@/components/base/Alert';
import { adminApi } from '@/lib/adminApi';
import type { AdminBank } from '@/lib/adminApi';
import { AdminCard, EmptyBlock, ErrorBlock, LoadingBlock, Modal, TableCell, TableHeaderCell } from './ui';

interface BankForm {
  id: number | null;
  name: string;
  country: string;
  swift: string;
  sortOrder: string;
  active: boolean;
}

const emptyForm: BankForm = { id: null, name: '', country: '', swift: '', sortOrder: '0', active: true };

export default function BanksTab({ token }: { token: string }) {
  const { t } = useTranslation();
  const [banks, setBanks] = useState<AdminBank[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<BankForm>(emptyForm);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      const res = await adminApi.banksList(token);
      if (!res.ok) {
        setFailed(true);
        return;
      }
      setBanks((res.banks ?? []) as AdminBank[]);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    if (busy) return;
    if (!form.name.trim()) {
      setError(t('admin.banks.nameRequired'));
      return;
    }
    setBusy(true);
    setError('');
    const res = await adminApi.bankSave(token, {
      bankId: form.id,
      name: form.name.trim(),
      country: form.country.trim(),
      swift: form.swift.trim(),
      sortOrder: Number(form.sortOrder || 0),
      active: form.active,
    });
    setBusy(false);
    if (!res.ok) {
      setError(t('errors.server'));
      return;
    }
    setOpen(false);
    setMessage(t('admin.banks.saved'));
    load();
  };

  const toggle = async (bank: AdminBank) => {
    setBusy(true);
    await adminApi.bankSave(token, {
      bankId: bank.id,
      name: bank.name,
      country: bank.country ?? '',
      swift: bank.swift ?? '',
      sortOrder: bank.sort_order,
      active: !bank.active,
    });
    setBusy(false);
    load();
  };

  return (
    <div className="flex flex-col gap-5">
      {message ? <Alert tone="success">{message}</Alert> : null}

      <AdminCard
        title={t('admin.banks.title')}
        subtitle={t('admin.banks.subtitle')}
        actions={
          <Button
            size="sm"
            onClick={() => {
              setForm(emptyForm);
              setError('');
              setOpen(true);
            }}
            icon={<i className="ri-add-line" aria-hidden="true" />}
          >
            {t('admin.banks.add')}
          </Button>
        }
        padded={false}
      >
        {loading ? (
          <LoadingBlock />
        ) : failed ? (
          <ErrorBlock onRetry={load} />
        ) : banks.length === 0 ? (
          <EmptyBlock label={t('admin.banks.empty')} icon="ri-bank-line" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[680px]">
              <thead className="bg-background-100">
                <tr>
                  <TableHeaderCell>{t('admin.banks.name')}</TableHeaderCell>
                  <TableHeaderCell>{t('admin.banks.country')}</TableHeaderCell>
                  <TableHeaderCell>{t('admin.banks.swift')}</TableHeaderCell>
                  <TableHeaderCell align="right">{t('admin.banks.order')}</TableHeaderCell>
                  <TableHeaderCell>{t('admin.common.status')}</TableHeaderCell>
                  <TableHeaderCell>{t('admin.common.actions')}</TableHeaderCell>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-200">
                {banks.map((bank) => (
                  <tr key={bank.id} className="hover:bg-background-100/60 transition-colors">
                    <TableCell>
                      <span className="font-semibold text-foreground-950">{bank.name}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-foreground-600">{bank.country ?? '—'}</span>
                    </TableCell>
                    <TableCell>
                      <span className="font-label text-[12px] text-foreground-600">{bank.swift ?? '—'}</span>
                    </TableCell>
                    <TableCell align="right">{bank.sort_order}</TableCell>
                    <TableCell>
                      <span
                        className={`inline-flex items-center whitespace-nowrap rounded-full border px-2.5 py-1 text-[11px] font-semibold font-label ${
                          bank.active
                            ? 'bg-primary-50 text-primary-800 border-primary-200'
                            : 'bg-background-200 text-foreground-600 border-background-300'
                        }`}
                      >
                        {bank.active ? t('admin.banks.activeLabel') : t('admin.banks.inactiveLabel')}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => {
                            setForm({
                              id: bank.id,
                              name: bank.name,
                              country: bank.country ?? '',
                              swift: bank.swift ?? '',
                              sortOrder: String(bank.sort_order ?? 0),
                              active: bank.active,
                            });
                            setError('');
                            setOpen(true);
                          }}
                          aria-label={t('admin.banks.edit')}
                          className="flex items-center justify-center w-8 h-8 rounded-full border border-background-300 text-foreground-600 hover:border-primary-400 hover:text-primary-700 transition-colors cursor-pointer"
                        >
                          <span className="flex items-center justify-center w-3.5 h-3.5">
                            <i className="ri-pencil-line text-[13px]" aria-hidden="true" />
                          </span>
                        </button>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => toggle(bank)}
                          aria-label={t('admin.banks.toggle')}
                          className="flex items-center justify-center w-8 h-8 rounded-full border border-background-300 text-foreground-600 hover:border-accent-400 hover:text-accent-700 transition-colors cursor-pointer disabled:opacity-50"
                        >
                          <span className="flex items-center justify-center w-3.5 h-3.5">
                            <i className={bank.active ? 'ri-eye-off-line text-[13px]' : 'ri-eye-line text-[13px]'} aria-hidden="true" />
                          </span>
                        </button>
                      </div>
                    </TableCell>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AdminCard>

      <Modal
        open={open}
        title={form.id ? t('admin.banks.edit') : t('admin.banks.add')}
        subtitle={t('admin.banks.subtitle')}
        onClose={() => setOpen(false)}
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
              {t('admin.common.cancel')}
            </Button>
            <Button size="sm" loading={busy} onClick={save}>
              {t('admin.banks.save')}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3.5">
          <Input
            label={t('admin.banks.name')}
            name="bankName"
            value={form.name}
            onChange={(event) => setForm({ ...form, name: event.target.value })}
            placeholder="BNP Paribas"
            icon="ri-bank-line"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
            <Input
              label={t('admin.banks.country')}
              name="bankCountry"
              value={form.country}
              onChange={(event) => setForm({ ...form, country: event.target.value })}
              placeholder="France"
              icon="ri-global-line"
            />
            <Input
              label={t('admin.banks.swift')}
              name="bankSwift"
              value={form.swift}
              onChange={(event) => setForm({ ...form, swift: event.target.value.toUpperCase() })}
              placeholder="BNPAFRPPXXX"
              icon="ri-hashtag"
              className="font-label"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 items-end">
            <Input
              label={t('admin.banks.order')}
              name="bankOrder"
              type="number"
              value={form.sortOrder}
              onChange={(event) => setForm({ ...form, sortOrder: event.target.value })}
            />
            <button
              type="button"
              onClick={() => setForm({ ...form, active: !form.active })}
              className={`inline-flex items-center justify-between gap-2 h-12 px-4 rounded-xl border text-[13px] font-semibold font-label transition-colors cursor-pointer ${
                form.active
                  ? 'border-primary-300 bg-primary-50 text-primary-800'
                  : 'border-background-300 bg-background-100 text-foreground-600'
              }`}
            >
              {t('admin.banks.active')}
              <span
                className={`flex items-center justify-center w-4 h-4 ${form.active ? 'text-primary-700' : 'text-foreground-400'}`}
              >
                <i className={form.active ? 'ri-checkbox-circle-fill text-base' : 'ri-checkbox-blank-circle-line text-base'} aria-hidden="true" />
              </span>
            </button>
          </div>
          {error ? <Alert tone="error">{error}</Alert> : null}
        </div>
      </Modal>
    </div>
  );
}